"use client";

import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Badge } from "@/app/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { 
  Upload, 
  Download, 
  Lock, 
  Unlock, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  Copy,
  Shuffle,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/app/lib/utils";

interface AlphabetFile {
  name: string;
  content: string;
  size: number;
}

export default function TextEncryptTool() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [alphabetFile, setAlphabetFile] = useState<AlphabetFile | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlphabet, setShowAlphabet] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate a random alphabet if no file is uploaded
  const generateRandomAlphabet = (): string => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;':\",./<>?";
    return chars.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) { // 1MB limit
      setError("File size must be less than 1MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (!content) {
        setError("Failed to read file content");
        return;
      }

      // Validate that the content contains only printable characters
      const cleanContent = content.replace(/[\r\n\t\s]/g, '');
      if (cleanContent.length < 26) {
        setError("Alphabet file must contain at least 26 unique characters");
        return;
      }

      // Remove duplicates and keep order
      const uniqueChars = Array.from(new Set(cleanContent.split(''))).join('');
      
      setAlphabetFile({
        name: file.name,
        content: uniqueChars,
        size: file.size
      });
      setError("");
      toast.success("Alphabet file loaded successfully");
    };

    reader.onerror = () => {
      setError("Failed to read file");
    };

    reader.readAsText(file);
  };

  const downloadAlphabetFile = () => {
    if (!alphabetFile) return;

    const blob = new Blob([alphabetFile.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alphabet-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Alphabet file downloaded");
  };

  const generateNewAlphabet = () => {
    const newAlphabet = generateRandomAlphabet();
    setAlphabetFile({
      name: "generated-alphabet.txt",
      content: newAlphabet,
      size: newAlphabet.length
    });
    toast.success("New random alphabet generated");
  };

  const createSubstitutionMap = (alphabet: string): Map<string, string> => {
    const map = new Map();
    const standardAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    
    for (let i = 0; i < Math.min(alphabet.length, standardAlphabet.length); i++) {
      map.set(standardAlphabet[i], alphabet[i]);
    }
    
    return map;
  };

  const createReverseSubstitutionMap = (alphabet: string): Map<string, string> => {
    const map = new Map();
    const standardAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    
    for (let i = 0; i < Math.min(alphabet.length, standardAlphabet.length); i++) {
      map.set(alphabet[i], standardAlphabet[i]);
    }
    
    return map;
  };

  const encryptText = (text: string, alphabet: string): string => {
    const substitutionMap = createSubstitutionMap(alphabet);
    let result = "";
    
    for (const char of text) {
      if (substitutionMap.has(char)) {
        result += substitutionMap.get(char);
      } else {
        result += char; // Keep non-alphabet characters as-is
      }
    }
    
    return result;
  };

  const decryptText = (text: string, alphabet: string): string => {
    const reverseMap = createReverseSubstitutionMap(alphabet);
    let result = "";
    
    for (const char of text) {
      if (reverseMap.has(char)) {
        result += reverseMap.get(char);
      } else {
        result += char; // Keep non-alphabet characters as-is
      }
    }
    
    return result;
  };

  const handleEncrypt = () => {
    if (!inputText.trim()) {
      setError("Please enter text to encrypt");
      return;
    }

    if (!alphabetFile) {
      setError("Please upload or generate an alphabet file first");
      return;
    }

    setIsProcessing(true);
    setError("");

    try {
      // All encryption happens client-side - no server communication
      const encrypted = encryptText(inputText, alphabetFile.content);
      setOutputText(encrypted);
      toast.success("Text encrypted successfully");
    } catch (err) {
      setError("Encryption failed");
      toast.error("Encryption failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecrypt = () => {
    if (!inputText.trim()) {
      setError("Please enter text to decrypt");
      return;
    }

    if (!alphabetFile) {
      setError("Please upload or generate an alphabet file first");
      return;
    }

    setIsProcessing(true);
    setError("");

    try {
      // All decryption happens client-side - no server communication
      const decrypted = decryptText(inputText, alphabetFile.content);
      setOutputText(decrypted);
      toast.success("Text decrypted successfully");
    } catch (err) {
      setError("Decryption failed");
      toast.error("Decryption failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Copied to clipboard");
    }
  };

  const clearClipboard = async () => {
    try {
      // Clear clipboard by copying empty string
      await navigator.clipboard.writeText("");
      toast.success("Clipboard cleared");
    } catch (err) {
      toast.error("Could not clear clipboard");
    }
  };

  const clearAll = () => {
    setInputText("");
    setOutputText("");
    setError("");
    // Clear any file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Clear alphabet file from memory
    setAlphabetFile(null);
    toast.success("All data cleared from memory");
  };

  // Clear sensitive data when component unmounts
  React.useEffect(() => {
    return () => {
      // Clear all sensitive data on unmount
      setInputText("");
      setOutputText("");
      setAlphabetFile(null);
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Security Notice */}
      <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
        <Lock className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800 dark:text-green-200">
          <strong>🔒 100% Client-Side Security:</strong> All encryption/decryption operations are performed 
          entirely in your browser. No data is transmitted to any server. Your text and alphabet files 
          never leave your device.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Text Encryption Tool
            <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
              Offline
            </Badge>
          </CardTitle>
          <CardDescription>
            Encrypt and decrypt text using a custom alphabet. All operations are performed locally in your browser - no data is sent to servers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Alphabet File Management */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Alphabet File</Label>
            
            {!alphabetFile ? (
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a text file containing your custom alphabet, or generate a random one
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload File
                  </Button>
                  <Button
                    variant="outline"
                    onClick={generateNewAlphabet}
                    className="flex items-center gap-2"
                  >
                    <Shuffle className="h-4 w-4" />
                    Generate Random
                  </Button>
                </div>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.text"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">{alphabetFile.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ({alphabetFile.size} bytes)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAlphabet(!showAlphabet)}
                    >
                      {showAlphabet ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={downloadAlphabetFile}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAlphabetFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                
                {showAlphabet && (
                  <div className="mt-2 p-2 bg-background rounded border text-xs font-mono break-all">
                    {alphabetFile.content}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground mt-2">
                  {alphabetFile.content.length} unique characters
                </p>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Text Input/Output */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="input-text">Input Text</Label>
              <Textarea
                id="input-text"
                placeholder="Enter text to encrypt or decrypt..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="min-h-[200px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="output-text">Output Text</Label>
              <Textarea
                id="output-text"
                placeholder="Encrypted/decrypted text will appear here..."
                value={outputText}
                readOnly
                className="min-h-[200px] bg-muted/50"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleEncrypt}
              disabled={isProcessing || !alphabetFile}
              className="flex items-center gap-2"
            >
              <Lock className="h-4 w-4" />
              Encrypt
            </Button>
            
            <Button
              onClick={handleDecrypt}
              disabled={isProcessing || !alphabetFile}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Unlock className="h-4 w-4" />
              Decrypt
            </Button>
            
            <Button
              onClick={() => copyToClipboard(outputText)}
              disabled={!outputText}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Copy className="h-4 w-4" />
              Copy Output
            </Button>
            
            <Button
              onClick={clearAll}
              variant="outline"
              className="flex items-center gap-2"
            >
              Clear All
            </Button>
            
            <Button
              onClick={clearClipboard}
              variant="outline"
              className="flex items-center gap-2"
            >
              Clear Clipboard
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">For Encryption:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Upload or generate an alphabet file</li>
                <li>Enter your text in the input field</li>
                <li>Click "Encrypt" to encode your message</li>
                <li>Share the encrypted text and alphabet file</li>
              </ol>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">For Decryption:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Upload the same alphabet file used for encryption</li>
                <li>Paste the encrypted text in the input field</li>
                <li>Click "Decrypt" to decode the message</li>
                <li>The original text will appear in the output</li>
              </ol>
            </div>
          </div>
          
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Privacy & Security:</strong> All encryption/decryption operations are performed 
              entirely in your browser. No data is sent to any server. Your text and alphabet files 
              never leave your device. This tool uses simple substitution cipher - for highly 
              sensitive data, consider using more advanced encryption methods.
            </AlertDescription>
          </Alert>
          
          <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
            <Lock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <strong>Client-Side Only:</strong> This tool operates completely offline. 
              Your data is processed locally in your browser and never transmitted over the internet. 
              Keep your alphabet file secure and never share it publicly.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
